/* src/components/About.tsx */
import React, { useRef, useEffect } from 'react';
import aboutStyles from '../styles/About.module.css';
import homeStyles from '../styles/HomePage.module.css';
import VCImage from '../resources/OIP.jpg';
import heroImage from '../resources/hero.png';

// Pie chart component
const PieChart: React.FC = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  
  // Department statistics data
  const stats = {
    undergraduates: 1420,
    phd: 286,
    professors: 74
  };
  
  const total = stats.undergraduates + stats.phd + stats.professors;
  
  // Calculate percentages
  const percentages = {
    undergraduates: Math.round((stats.undergraduates / total) * 100),
    phd: Math.round((stats.phd / total) * 100),
    professors: Math.round((stats.professors / total) * 100)
  };
  
  // Color scheme
  const colors = {
    undergraduates: '#4e79a7',
    phd: '#f28e2c',
    professors: '#e15759'
  };

  // Draw the pie chart
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const radius = Math.min(centerX, centerY) - 10;
    
    // Clear canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    // Draw pie slices
    let startAngle = 0;
    
    // Undergraduates slice
    const undergradAngle = (percentages.undergraduates / 100) * Math.PI * 2;
    ctx.beginPath();
    ctx.moveTo(centerX, centerY);
    ctx.arc(centerX, centerY, radius, startAngle, startAngle + undergradAngle);
    ctx.closePath();
    ctx.fillStyle = colors.undergraduates;
    ctx.fill();
    startAngle += undergradAngle;
    
    // PhD slice
    const phdAngle = (percentages.phd / 100) * Math.PI * 2;
    ctx.beginPath();
    ctx.moveTo(centerX, centerY);
    ctx.arc(centerX, centerY, radius, startAngle, startAngle + phdAngle);
    ctx.closePath();
    ctx.fillStyle = colors.phd;
    ctx.fill();
    startAngle += phdAngle;
    
    // Professors slice
    const profAngle = (percentages.professors / 100) * Math.PI * 2;
    ctx.beginPath();
    ctx.moveTo(centerX, centerY);
    ctx.arc(centerX, centerY, radius, startAngle, startAngle + profAngle);
    ctx.closePath();
    ctx.fillStyle = colors.professors;
    ctx.fill();
    
    // Draw center hole for donut effect
    ctx.beginPath();
    ctx.moveTo(centerX, centerY);
    ctx.arc(centerX, centerY, radius * 0.5, 0, Math.PI * 2);
    ctx.closePath();
    ctx.fillStyle = 'white';
    ctx.fill();
    
    // Draw center text
    ctx.fillStyle = '#2c3e50';
    ctx.font = 'bold 16px "Segoe UI", sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText('Total', centerX, centerY - 10);
    ctx.font = 'bold 24px "Segoe UI", sans-serif';
    ctx.fillText(total.toString(), centerX, centerY + 15);
  }, [percentages, colors, total]);

  return (
    <div className={aboutStyles['pie-chart-container']}>
      <div className={aboutStyles['chart-header']}>
        <h2 className={aboutStyles['chart-title']}>Department Composition</h2>
        <p className={aboutStyles['chart-subtitle']}>Academic Year 2025/2026</p>
      </div>
      
      <div className={aboutStyles['chart-content']}>
        <div className={aboutStyles['chart-canvas']}>
          <canvas ref={canvasRef} width={300} height={300} />
        </div>
        
        <div className={aboutStyles['chart-legend']}>
          <div className={aboutStyles['legend-item']}>
            <div className={aboutStyles['legend-color']} style={{ backgroundColor: colors.undergraduates }}></div>
            <div className={aboutStyles['legend-info']}>
              <span className={aboutStyles['legend-label']}>Undergraduates</span>
              <div className={aboutStyles['legend-values']}>
                <span className={aboutStyles['legend-value']}>{stats.undergraduates}</span>
                <span className={aboutStyles['legend-percentage']}>{percentages.undergraduates}%</span>
              </div>
            </div>
          </div>
          
          <div className={aboutStyles['legend-item']}>
            <div className={aboutStyles['legend-color']} style={{ backgroundColor: colors.phd }}></div>
            <div className={aboutStyles['legend-info']}>
              <span className={aboutStyles['legend-label']}>PhD Students</span>
              <div className={aboutStyles['legend-values']}>
                <span className={aboutStyles['legend-value']}>{stats.phd}</span>
                <span className={aboutStyles['legend-percentage']}>{percentages.phd}%</span>
              </div>
            </div>
          </div>
          
          <div className={aboutStyles['legend-item']}>
            <div className={aboutStyles['legend-color']} style={{ backgroundColor: colors.professors }}></div>
            <div className={aboutStyles['legend-info']}>
              <span className={aboutStyles['legend-label']}>Professors</span>
              <div className={aboutStyles['legend-values']}>
                <span className={aboutStyles['legend-value']}>{stats.professors}</span>
                <span className={aboutStyles['legend-percentage']}>{percentages.professors}%</span>
              </div>
            </div>
          </div>
        </div>
      </div>
      
      <div className={aboutStyles['chart-insight']}>
        <div className={aboutStyles['insight-card']}>
          <h3>Student-to-Professor Ratio</h3>
          <p>{Math.round((stats.undergraduates + stats.phd) / stats.professors)}:1</p>
        </div>
        <div className={aboutStyles['insight-card']}>
          <h3>Graduate Percentage</h3>
          <p>{Math.round((stats.phd / (stats.undergraduates + stats.phd)) * 100)}%</p>
        </div>
      </div>
    </div>
  );
};

interface Leader {
  id: number;
  name: string;
  title: string;
  bio: string;
  department: string;
  imageUrl: string;
}

const leaders: Leader[] = [
  {
    id: 1,
    name: 'Prof. Florence Obi',
    title: 'Vice Chancellor',
    bio: 'Distinguished scholar with 25+ years in academic leadership. PhD in Educational Psychology.',
    department: 'University Administration',
    imageUrl: VCImage,
  },
  {
    id: 2,
    name: 'Dr. Emmanuel Eyo',
    title: 'Dean of Humanities',
    bio: 'Expert in African philosophy with numerous publications on indigenous knowledge systems.',
    department: 'Faculty of Humanities',
    imageUrl: VCImage,
  },
  {
    id: 3,
    name: 'Prof. Anya Nkachi',
    title: 'Head of Department',
    bio: "Leading scholar in contemporary metaphysics and author of 'The Fabric of Reality'.",
    department: 'Philosophy Department',
    imageUrl: VCImage,
  },
  {
    id: 4,
    name: 'Dr. Chiedozie Okoro',
    title: 'Deputy HOD',
    bio: 'Specializes in applied ethics and moral philosophy with focus on post-colonial societies.',
    department: 'Philosophy Department',
    imageUrl: VCImage,
  },
  {
    id: 5,
    name: 'Prof. Anya Nkachi',
    title: 'Head of Department',
    bio: "Leading scholar in contemporary metaphysics and author of 'The Fabric of Reality'.",
    department: 'Philosophy Department',
    imageUrl: VCImage,
  },
  {
    id: 6,
    name: 'Dr. Chiedozie Okoro',
    title: 'Deputy HOD',
    bio: 'Specializes in applied ethics and moral philosophy with focus on post-colonial societies.',
    department: 'Philosophy Department',
    imageUrl: VCImage,
  },
];

const About: React.FC = () => (
  <div className={`${aboutStyles['about-page']} ${homeStyles['home-page']}`}>
    {/* Hero Section */}
    <section
      className={aboutStyles['about-hero']}
      style={{ backgroundImage: `url(${heroImage})` }}
    >
      <div className={aboutStyles['hero-overlay']}>
        <div className={aboutStyles['hero-content']}>
          <h1>About Our Philosophy Department</h1>
          <p>Exploring the fundamental questions of human existence since 1975</p>
        </div>
      </div>
    </section>

    {/* Mission & Vision */}
    <section className={aboutStyles['mission-section']}>
      <div className={`${aboutStyles['container2']} ${homeStyles.container}`}>
        <div className={aboutStyles['mission-content']}>
          <div className={aboutStyles['mission-text']}>
            <div className={aboutStyles['section-header']}>
              <h2 className={aboutStyles['section-title']}>Our Mission & Vision</h2>
              <div className={aboutStyles.divider} />
            </div>
            <div className={aboutStyles['mission-cards']}>
              <div className={aboutStyles['mission-card']}>
                <div className={aboutStyles['card-icon']}>
                  <svg viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z"/>
                  </svg>
                </div>
                <div className={aboutStyles['card-content']}>
                  <h3>Our Mission</h3>
                  <p>
                    The Department of Philosophy at the University of Calabar is dedicated to cultivating 
                    critical thinking and fostering deep engagement with the fundamental questions of human existence. 
                    We strive to create an environment where students develop the analytical skills to examine 
                    complex issues from multiple perspectives.
                  </p>
                </div>
              </div>
              <div className={aboutStyles['mission-card']}>
                <div className={aboutStyles['card-icon']}>
                  <svg viewBox="0 0 24 24">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-1-13h2v6h-2zm0 8h2v2h-2z"/>
                  </svg>
                </div>
                <div className={aboutStyles['card-content']}>
                  <h3>Our Vision</h3>
                  <p>
                    Our vision is to be a leading center for philosophical inquiry in Africa, known for our innovative 
                    research and commitment to addressing contemporary challenges through philosophical frameworks.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className={aboutStyles['mission-visual']}>
            <div className={aboutStyles['visual-container']}>
              <div className={aboutStyles['floating-shapes']}>
                <div className={`${aboutStyles.shape} ${aboutStyles.circle}`} />
                <div className={`${aboutStyles.shape} ${aboutStyles.triangle}`} />
                <div className={`${aboutStyles.shape} ${aboutStyles.square}`} />
              </div>
              <div className={aboutStyles['image-wrapper']}>
                <div className={aboutStyles['image-frame']} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* History Section */}
    <section className={aboutStyles['history-section']}>
      <div className={`${aboutStyles.container1} ${homeStyles.container}`}>
        <h2>Our History</h2>
        <div className={aboutStyles.divider} />
        <div className={aboutStyles.timeline}>
          <div className={aboutStyles['timeline-item']}>
            <div className={aboutStyles['timeline-year']}>1975</div>
            <div className={aboutStyles['timeline-content']}>
              <h3>Department Founded</h3>
              <p>The Department of Philosophy was established with just 3 faculty members and 28 students.</p>
            </div>
          </div>
          <div className={aboutStyles['timeline-item']}>
            <div className={aboutStyles['timeline-year']}>1988</div>
            <div className={aboutStyles['timeline-content']}>
              <h3>First Graduate Program</h3>
              <p>Launched our MA program, becoming one of the first philosophy departments in West Africa to offer graduate studies.</p>
            </div>
          </div>
          <div className={aboutStyles['timeline-item']}>
            <div className={aboutStyles['timeline-year']}>2002</div>
            <div className={aboutStyles['timeline-content']}>
              <h3>New Building Inaugurated</h3>
              <p>Moved to our current location in the Humanities Building with state-of-the-art facilities.</p>
            </div>
          </div>
          <div className={aboutStyles['timeline-item']}>
            <div className={aboutStyles['timeline-year']}>2015</div>
            <div className={aboutStyles['timeline-content']}>
              <h3>Center for African Philosophy</h3>
              <p>Established a research center dedicated to African philosophical traditions.</p>
            </div>
          </div>
          <div className={aboutStyles['timeline-item']}>
            <div className={aboutStyles['timeline-year']}>Present</div>
            <div className={aboutStyles['timeline-content']}>
              <h3>Continuing Excellence</h3>
              <p>Now with 22 faculty members and over 300 students across undergraduate and graduate programs.</p>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* Values Section */}
    <section className={aboutStyles['values-section']}>
      <div className={`${aboutStyles.container} ${homeStyles.container}`}>
        <h2>Our Core Values</h2>
        <div className={aboutStyles.divider} />
        <div className={aboutStyles['values-grid']}>
          <div className={aboutStyles['value-card']}>
            <div className={aboutStyles['value-icon']}><i className="fas fa-brain" /></div>
            <h3>Critical Thinking</h3>
            <p>We cultivate rigorous analytical skills and encourage questioning assumptions.</p>
          </div>
          <div className={aboutStyles['value-card']}>
            <div className={aboutStyles['value-icon']}><i className="fas fa-hands-helping" /></div>
            <h3>Ethical Engagement</h3>
            <p>We promote moral reasoning and its application to contemporary issues.</p>
          </div>
          <div className={aboutStyles['value-card']}>
            <div className={aboutStyles['value-icon']}><i className="fas fa-globe-africa" /></div>
            <h3>Cultural Diversity</h3>
            <p>We embrace philosophical traditions from around the world, especially African perspectives.</p>
          </div>
          <div className={aboutStyles['value-card']}>
            <div className={aboutStyles['value-icon']}><i className="fas fa-lightbulb" /></div>
            <h3>Intellectual Curiosity</h3>
            <p>We foster a lifelong passion for learning and exploring fundamental questions.</p>
          </div>
        </div>
      </div>
    </section>

    {/* Academic Leaders */}
    <section className={aboutStyles['academic-leaders']}>
      <div className={`${aboutStyles['leadership-container']} ${homeStyles.container}`}>
        <div className={aboutStyles['leadership-header']}>
          <h2 className={aboutStyles['leadership-title']}>Academic Leadership</h2>
          <div className={aboutStyles['title-divider']} />
          <p className={aboutStyles['leadership-subtitle']}>
            Meet our distinguished academic leaders guiding the Philosophy Department
          </p>
        </div>
        <div className={aboutStyles['leaders-grid']}>
          {leaders.map((leader) => (
            <div className={aboutStyles['leader-profile']} key={leader.id}>
              <div className={aboutStyles['leader-img-container']}>
                <img
                  src={leader.imageUrl}
                  alt={leader.name}
                  className={aboutStyles['leader-photo']}
                />
                <span className={aboutStyles['leader-department']}>
                  {leader.department}
                </span>
              </div>
              <div className={aboutStyles['leader-details']}>
                <h3 className={aboutStyles['leader-name']}>{leader.name}</h3>
                <p className={aboutStyles['leader-position']}>{leader.title}</p>
                <p className={aboutStyles['leader-bio']}>{leader.bio}</p>
                <button className={aboutStyles['contact-button']}>
                  Contact
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  >
                    <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
                    <polyline points="22,6 12,13 2,6" />
                  </svg>
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Department Statistics Pie Chart - Placed lower on the page */}
    <section className={aboutStyles['department-stats']}>
      <PieChart />
    </section>

    {/* Testimonials */}
    <section className={aboutStyles['testimonials-section']}>
      <div className={`${aboutStyles.container} ${homeStyles.container}`}>
        <h2>What Our Students Say</h2>
        <div className={aboutStyles.divider} />
        <div className={aboutStyles['testimonials-grid']}>
          <div className={aboutStyles['testimonial-card']}>
            <div className={aboutStyles['testimonial-text']}>
              "The Philosophy Department transformed how I think about the world. The faculty's dedication to student success is unparalleled."
            </div>
            <div className={aboutStyles['testimonial-author']}>
              <div className={aboutStyles['author-image']} />
              <div className={aboutStyles['author-info']}>
                <h4>Chiamaka Eze</h4>
                <p>PhD Candidate</p>
              </div>
            </div>
          </div>
          <div className={aboutStyles['testimonial-card']}>
            <div className={aboutStyles['testimonial-text']}>
              "Studying philosophy here gave me critical thinking skills that have been invaluable in my legal career."
            </div>
            <div className={aboutStyles['testimonial-author']}>
              <div className={aboutStyles['author-image']} />
              <div className={aboutStyles['author-info']}>
                <h4>Biodun Adewale</h4>
                <p>Alumnus, Class of 2010</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
);

export default About;